import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CalAngularService } from '@cvx/cal-angular';
import { catchError, firstValueFrom, forkJoin, map, of } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CurrentUserProfile {
  objectId: string;
  name: string;
  positionName: string;
  department: string;
  initials: string;
  email: string;
  roles: string[];
}

const FALLBACK: CurrentUserProfile = {
  objectId: '',
  name: '',
  positionName: '',
  department: '',
  initials: '',
  email: '',
  roles: [],
};

function normalizeRoleKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

interface MeResponseDto {
  userName?: string | null;
  displayName?: string | null;
  email?: string | null;
  tenantId?: string | null;
}

interface PswUserDetailsDto {
  englishFirstName?: string | null;
  englishLastName?: string | null;
  positionName?: string | null;
  positionDescription?: string | null;
}

interface MeRolesDto {
  userId?: string | null;
  name?: string | null;
  email?: string | null;
  isAuthenticated?: boolean;
  roles?: string[] | null;
}

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private readonly http = inject(HttpClient);
  private readonly cal = inject(CalAngularService);
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly _profile = signal<CurrentUserProfile>(FALLBACK);
  private _loaded = false;
  private _loadPromise: Promise<void> | null = null;

  readonly profile = this._profile.asReadonly();

  async load(): Promise<void> {
    if (this._loaded) return;
    if (this._loadPromise) {
      await this._loadPromise;
      return;
    }

    this._loadPromise = (async () => {
      try {
        const me$ = this.http.get<MeResponseDto>(`${this.apiBaseUrl}/api/Me/me`, {
          withCredentials: true,
        });
        const roles$ = this.http
          .get<MeRolesDto>(`${this.apiBaseUrl}/api/Me/roles`, {
            withCredentials: true,
          })
          .pipe(
            map(response => response ?? {}),
            catchError(() => of({} as MeRolesDto)),
          );

        const { me, meRoles } = await firstValueFrom(
          forkJoin({
            me: me$,
            meRoles: roles$,
          }),
        );

        const rawName = me.displayName?.trim() || me.userName?.trim() || '';
        const name = rawName
          .replace(/\[.*?\]/g, '')
          .replace(/,/g, '')
          .replace(/\s+/g, ' ')
          .trim();

        const department = '';
        const email = (me.email ?? '').trim();
        const objectId = (meRoles.userId ?? '').trim();
        const roles = Array.from(new Set((meRoles.roles ?? []).map((role: string) => role.trim()).filter(Boolean)));
        const initials = name
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((part: string) => part[0]?.toUpperCase() ?? '')
          .join('');

        this._profile.set({ objectId, name, positionName: '', department, initials, email, roles });

        void firstValueFrom(
          this.http.get<PswUserDetailsDto>(`${this.apiBaseUrl}/api/Me/pswsUserDetails`, {
            withCredentials: true,
          }).pipe(map(response => response ?? {})),
        ).then((pswUserDetails) => {
          const currentProfile = this._profile();
          const englishFirstName = pswUserDetails.englishFirstName?.trim() ?? '';
          const englishLastName = pswUserDetails.englishLastName?.trim() ?? '';
          const fullEnglishName = `${englishFirstName} ${englishLastName}`.trim();
          const positionName = (pswUserDetails.positionName ?? pswUserDetails.positionDescription ?? '').trim();
          const nameWithDetails = (fullEnglishName || currentProfile.name)
            .replace(/\[.*?\]/g, '')
            .replace(/,/g, '')
            .replace(/\s+/g, ' ')
            .trim();

          this._profile.set({
            ...currentProfile,
            name: nameWithDetails,
            positionName,
            initials: nameWithDetails
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((part: string) => part[0]?.toUpperCase() ?? '')
              .join(''),
          });
        }).catch(() => undefined);
      } catch (error) {
        console.error(error);
        // Fallback to account info if Graph call fails
        const account = this.cal.getAccount();
        if (account) {
          const name = account.name ?? '';
          const initials = name
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part: string) => part[0]?.toUpperCase() ?? '')
            .join('');

          const idTokenClaims = (account as { idTokenClaims?: Record<string, unknown> }).idTokenClaims;
          const claimRolesRaw = idTokenClaims?.['roles'];
          const claimRoles = Array.isArray(claimRolesRaw)
            ? claimRolesRaw.filter((value): value is string => typeof value === 'string').map(value => value.trim()).filter(Boolean)
            : [];

          this._profile.set({
            objectId: '',
            name,
            positionName: '',
            department: '',
            initials,
            email: account.username ?? '',
            roles: claimRoles,
          });
        }
      } finally {
        this._loaded = true;
        this._loadPromise = null;
      }
    })();

    await this._loadPromise;
  }

  hasAnyRole(requiredRoles: readonly string[]): boolean {
    const availableRoleKeys = this._profile().roles
      .map(role => normalizeRoleKey(role))
      .filter(Boolean);

    return requiredRoles.some((requiredRole) => {
      const normalizedRequired = normalizeRoleKey(requiredRole);
      if (!normalizedRequired) {
        return false;
      }

      return availableRoleKeys.some((availableRole) => (
        availableRole === normalizedRequired
        || availableRole.endsWith(`_${normalizedRequired}`)
      ));
    });
  }
}

